import React, { useState, useCallback, useRef, useEffect } from 'react';
import { TTSService } from '../../services/tts/ttsService';
import { TTSVoice } from '../../services/tts/types';
import './TTSControls.css';

interface TTSControlsProps {
  ttsService: TTSService;
  onClose: () => void;
  onMinimize: () => void;
  isMinimized: boolean;
  speak: (text: string) => Promise<void>;
}

const TTSControls: React.FC<TTSControlsProps> = ({
  ttsService,
  onClose,
  onMinimize,
  isMinimized,
  speak,
}) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [speed, setSpeed] = useState(1.0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isCentered, setIsCentered] = useState(true);
  const [voices, setVoices] = useState<TTSVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState('');
  const [statusText, setStatusText] = useState('');
  const controlsRef = useRef<HTMLDivElement>(null);
  const posRef = useRef({ x: 0, y: 20 });
  const wakeLockRef = useRef<any>(null);
  const autoReadRef = useRef(false);
  const ttsSpeakingRef = useRef(false);

  // 同步 ttsSpeaking 状态到 ref（用于回调中读取最新值）
  useEffect(() => {
    ttsSpeakingRef.current = isSpeaking;
  }, [isSpeaking]);

  // 加载语音列表
  useEffect(() => {
    const loadVoices = () => {
      try {
        const v = ttsService.getVoices();
        setVoices(v);
        // 设置默认语音
        if (!selectedVoice && v.length > 0) {
          const defaultVoice = v.find(voice => voice.default) || v[0];
          setSelectedVoice(defaultVoice.name);
          ttsService.updateConfig({ voiceName: defaultVoice.name });
        }
      } catch (e) {
        console.warn('[TTS] 加载语音列表失败:', e);
      }
    };

    loadVoices();
    // 某些浏览器语音列表是异步加载的
    try {
      const synth = (window as any).speechSynthesis || (window as any).webkitSpeechSynthesis;
      if (synth) {
        synth.onvoiceschanged = loadVoices;
      }
    } catch (e) {
      console.warn('[TTS] onvoiceschanged 设置失败:', e);
    }

    return () => {
      try {
        const synth = (window as any).speechSynthesis || (window as any).webkitSpeechSynthesis;
        if (synth) {
          synth.onvoiceschanged = null;
        }
      } catch (e) { /* 忽略 */ }
    };
  }, [ttsService, selectedVoice]);

  // 监听 TTS 状态变化
  useEffect(() => {
    const handleStart = () => {
      setIsSpeaking(true);
      setIsPaused(false);
      setStatusText('');
    };

    const handleEnd = () => {
      setIsSpeaking(false);
      setIsPaused(false);
      setProgress(0);
      clearHighlight();
    };

    const handlePause = () => setIsPaused(true);
    const handleResume = () => setIsPaused(false);

    // 跟读高亮：根据 boundary 事件携带的句子调用原生高亮
    const handleBoundary = (event: { data?: { sentence?: string | null } }) => {
      const sentence = event.data?.sentence;
      if (sentence === undefined) return; // 仅心跳，无句子信息
      if (sentence === null || sentence.trim().length === 0) {
        clearHighlight();
      } else {
        applyHighlight(sentence);
      }
    };

    ttsService.on('start', handleStart);
    ttsService.on('end', handleEnd);
    ttsService.on('pause', handlePause);
    ttsService.on('resume', handleResume);
    ttsService.on('boundary', handleBoundary);

    return () => {
      ttsService.off('start', handleStart);
      ttsService.off('end', handleEnd);
      ttsService.off('pause', handlePause);
      ttsService.off('resume', handleResume);
      ttsService.off('boundary', handleBoundary);
    };
  }, [ttsService]);

  // 页面可见性变化时暂停/恢复 TTS（问题4：熄屏处理）
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!ttsSpeakingRef.current) return;

      if (document.hidden) {
        console.log('[TTS] 页面隐藏');
      } else {
        console.log('[TTS] 页面恢复可见，尝试恢复TTS');
        try {
          const synth = (window as any).speechSynthesis || (window as any).webkitSpeechSynthesis;
          if (synth) synth.resume();
        } catch (e) {
          console.warn('[TTS] 恢复TTS失败:', e);
        }
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // 请求屏幕唤醒锁（问题4：防止熄屏）
  const requestWakeLock = useCallback(async () => {
    try {
      const nav = navigator as any;
      if (nav.wakeLock) {
        wakeLockRef.current = await nav.wakeLock.request('screen');
        wakeLockRef.current.addEventListener('release', () => {
          console.log('[TTS] 唤醒锁已释放');
        });
        console.log('[TTS] 已获取屏幕唤醒锁');
      }
    } catch (e) {
      console.warn('[TTS] 无法获取唤醒锁:', e);
    }
  }, []);

  // 释放屏幕唤醒锁
  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
      } catch (e) {
        // 忽略
      }
      wakeLockRef.current = null;
    }
  }, []);

  // 组件卸载时释放唤醒锁
  useEffect(() => {
    return () => {
      releaseWakeLock();
    };
  }, [releaseWakeLock]);

  // 触摸/鼠标拖拽
  const handleDragStart = useCallback((clientX: number, clientY: number) => {
    if (!controlsRef.current) return;

    if (isCentered) {
      const rect = controlsRef.current.getBoundingClientRect();
      posRef.current = { x: rect.left, y: rect.top };
      setIsCentered(false);
      setDragOffset({ x: clientX - rect.left, y: clientY - rect.top });
    } else {
      setDragOffset({ x: clientX - posRef.current.x, y: clientY - posRef.current.y });
    }
    setIsDragging(true);
  }, [isCentered]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.target instanceof HTMLElement && e.target.closest('.tts-btn')) {
      return;
    }
    handleDragStart(e.clientX, e.clientY);
  }, [handleDragStart]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.target instanceof HTMLElement && e.target.closest('.tts-btn')) {
      return;
    }
    const touch = e.touches[0];
    handleDragStart(touch.clientX, touch.clientY);
  }, [handleDragStart]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      const maxX = window.innerWidth - (controlsRef.current?.offsetWidth || 300);
      const maxY = window.innerHeight - (controlsRef.current?.offsetHeight || 50);
      posRef.current = {
        x: Math.max(0, Math.min(maxX, newX)),
        y: Math.max(0, Math.min(maxY, newY)),
      };
      if (controlsRef.current) {
        controlsRef.current.style.left = posRef.current.x + 'px';
        controlsRef.current.style.top = posRef.current.y + 'px';
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.touches[0];
      const newX = touch.clientX - dragOffset.x;
      const newY = touch.clientY - dragOffset.y;
      const maxX = window.innerWidth - (controlsRef.current?.offsetWidth || 300);
      const maxY = window.innerHeight - (controlsRef.current?.offsetHeight || 50);
      posRef.current = {
        x: Math.max(0, Math.min(maxX, newX)),
        y: Math.max(0, Math.min(maxY, newY)),
      };
      if (controlsRef.current) {
        controlsRef.current.style.left = posRef.current.x + 'px';
        controlsRef.current.style.top = posRef.current.y + 'px';
      }
    };

    const handleEnd = () => setIsDragging(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, dragOffset]);

  // === iframe / koodo-reader 访问 ===

  // 获取外层 iframe（Koodo Reader 应用）的 window
  const getReaderWindow = useCallback((): Window | null => {
    try {
      const iframe = document.querySelector('iframe');
      if (!iframe || !iframe.contentWindow) return null;
      return iframe.contentWindow;
    } catch (e) {
      return null;
    }
  }, []);

  // 获取内层 iframe 的 window（epub 正文渲染 iframe，用于 innerText 回退）
  const getInnerWindow = useCallback((): Window | null => {
    try {
      const iframe = document.querySelector('iframe');
      if (!iframe || !iframe.contentDocument) return null;
      const innerIframe = iframe.contentDocument.querySelector('iframe');
      if (!innerIframe || !innerIframe.contentWindow) return null;
      return innerIframe.contentWindow;
    } catch (e) {
      return null;
    }
  }, []);

  // 从 Koodo 应用的 React fiber 树中查找 Redux store
  const findStoreFromFiber = useCallback((readerWin: Window): any => {
    try {
      const rootEl = (readerWin.document as any).getElementById('root');
      if (!rootEl) return null;

      let fiberRoot = null;
      // React 16/17 legacy root: _reactRootContainer._internalRoot.current
      if (rootEl._reactRootContainer && rootEl._reactRootContainer._internalRoot) {
        fiberRoot = rootEl._reactRootContainer._internalRoot.current;
      }
      // React 18 createRoot: __reactContainer$xxx
      if (!fiberRoot) {
        for (const key of Object.keys(rootEl)) {
          if (key.indexOf('__reactContainer$') === 0) { fiberRoot = rootEl[key]; break; }
        }
      }
      if (!fiberRoot) {
        for (const key of Object.keys(rootEl)) {
          if (key.indexOf('__reactFiber$') === 0) { fiberRoot = rootEl[key]; break; }
        }
      }
      if (!fiberRoot) return null;

      const queue = [fiberRoot];
      const visited = new Set();
      let count = 0;
      while (queue.length > 0) {
        const fiber = queue.shift();
        if (!fiber || visited.has(fiber)) continue;
        visited.add(fiber);
        if (++count > 5000) break;

        if (fiber.memoizedProps && fiber.memoizedProps.store) return fiber.memoizedProps.store;
        if (fiber.stateNode && fiber.stateNode.store) return fiber.stateNode.store;
        if (fiber.child) queue.push(fiber.child);
        if (fiber.sibling) queue.push(fiber.sibling);
      }
      return null;
    } catch (e) {
      console.warn('[TTSControls] 遍历 fiber 树失败:', e);
      return null;
    }
  }, []);

  // 获取 epub.js rendition：优先 window.rendition（RN WebView 分支），其次 ttsBridge.getRendition()，
  // 最后直接遍历 Koodo 应用 fiber 树从 Redux store 读取 reader.htmlBook.rendition（网页版）
  const getRendition = useCallback((): any => {
    const readerWin = getReaderWindow();
    if (!readerWin) {
      console.warn('[TTSControls] 无法获取 reader window');
      return null;
    }
    try {
      // 方式1：RN WebView 分支暴露的全局变量
      if ((readerWin as any).rendition) {
        console.log('[TTSControls] ✓ 从 window.rendition 获取 rendition');
        return (readerWin as any).rendition;
      }

      // 方式2：ttsBridge.getRendition()（若 tts-bridge.js 已加载）
      const bridge = (readerWin as any).ttsBridge;
      if (bridge && typeof bridge.getRendition === 'function') {
        try {
          const r = bridge.getRendition();
          if (r) {
            console.log('[TTSControls] ✓ 通过 ttsBridge 获取 rendition');
            return r;
          }
        } catch (e2) {
          console.warn('[TTSControls] ttsBridge.getRendition() 失败:', e2);
        }
      }

      // 方式3：直接遍历 fiber 树从 Redux store 读取
      const store = findStoreFromFiber(readerWin);
      if (store && store.getState) {
        const state = store.getState();
        const htmlBook = state && state.reader ? state.reader.htmlBook : null;
        if (htmlBook && htmlBook.rendition) {
          console.log('[TTSControls] ✓ 从 Redux store 获取 rendition');
          return htmlBook.rendition;
        }
        console.warn('[TTSControls] store 中 reader.htmlBook 为空');
      } else {
        console.warn('[TTSControls] 未找到 Redux store');
      }
    } catch (e) {
      console.warn('[TTSControls] 获取 rendition 失败:', e);
    }
    return null;
  }, [getReaderWindow, findStoreFromFiber]);

  // 高亮当前朗读句子（复用 Koodo Reader 原生跟读高亮）
  const applyHighlight = useCallback((sentence: string) => {
    const rendition = getRendition();
    if (!rendition) return;
    try {
      if (typeof rendition.highlightAudioNode === 'function') {
        rendition.highlightAudioNode(sentence, 'background: #f3a6a68c;');
        console.log('[TTSControls] ✓ 跟读高亮:', sentence);
      } else {
        console.warn('[TTSControls] rendition.highlightAudioNode 不可用');
      }
    } catch (e) {
      console.warn('[TTSControls] 跟读高亮失败:', e);
    }
  }, [getRendition]);

  // 清除跟读高亮（原生实现：先清除上一句 span，再空句子则跳过定位）
  const clearHighlight = useCallback(() => {
    const rendition = getRendition();
    if (!rendition) return;
    try {
      if (typeof rendition.highlightAudioNode === 'function') {
        rendition.highlightAudioNode('', '');
        console.log('[TTSControls] ✓ 已清除跟读高亮');
      }
    } catch (e) {
      console.warn('[TTSControls] 清除跟读高亮失败:', e);
    }
  }, [getRendition]);

  // 从 koodo-reader 提取当前页文本
  const extractPageText = useCallback(async (): Promise<string> => {
    // 方式1：使用 rendition.audioText()
    const rendition = getRendition();
    if (rendition) {
      try {
        if (typeof rendition.audioText === 'function') {
          const textArray = await rendition.audioText();
          if (textArray && Array.isArray(textArray) && textArray.length > 0) {
            const text = textArray
              .filter((t: string) => t && t.trim())
              .map((t: string) => t.trim())
              .join('\n');
            if (text.length > 10) {
              console.log('[TTSControls] ✓ rendition.audioText() 长度:', text.length);
              return text;
            }
          }
        }
      } catch (e) {
        console.warn('[TTSControls] rendition.audioText() 失败:', e);
      }
    }

    // 方式2：回退到 innerText（内层 epub 正文 iframe）
    const innerWin = getInnerWindow();
    if (!innerWin) {
      console.error('[TTSControls] 无法获取内层 iframe window');
      return '';
    }
    try {
      const targetDoc = innerWin.document;
      const bodyText = (targetDoc.body as HTMLElement)?.innerText || targetDoc.body?.textContent || '';
      const cleanText = bodyText
        .replace(/第\d+页/g, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
      console.log('[TTSControls] innerText 回退，长度:', cleanText.length);
      return cleanText;
    } catch (e) {
      console.error('[TTSControls] innerText 提取失败:', e);
      return '';
    }
  }, [getRendition, getInnerWindow]);

  // 翻到下一页
  const goToNextPage = useCallback(async (): Promise<boolean> => {
    const rendition = getRendition();
    if (!rendition) return false;

    try {
      if (typeof rendition.next !== 'function') {
        console.warn('[TTSControls] rendition.next 不可用');
        return false;
      }

      await rendition.next();
      console.log('[TTSControls] ✓ rendition.next() 完成');
      return true;
    } catch (e) {
      console.error('[TTSControls] 翻页失败:', e);
      return false;
    }
  }, [getRendition]);

  // 等待翻页渲染（使用延时而非 rendered 事件，避免旋转屏幕时的竞争条件）
  const waitForPageLoad = useCallback((): Promise<void> => {
    return new Promise(resolve => {
      // 简单延时：给 koodo-reader 足够时间渲染新页面
      setTimeout(resolve, 800);
    });
  }, []);

  // === 朗读操作 ===

  // 检查 speechSynthesis 是否真正可用
  const checkSynthAvailable = useCallback((): boolean => {
    const synth = (window as any).speechSynthesis || (window as any).webkitSpeechSynthesis;
    return !!synth;
  }, []);

  // 朗读当前页
  const handleReadPage = useCallback(async () => {
    if (!checkSynthAvailable()) {
      setStatusText('当前浏览器不支持语音朗读');
      console.warn('[TTSControls] speechSynthesis 不可用');
      return;
    }
    console.log('[TTSControls] 朗读当前页');
    setStatusText('提取文本...');
    const text = await extractPageText();
    if (text) {
      await requestWakeLock();
      setStatusText('');
      speak(text);
    } else {
      setStatusText('未找到文本');
      console.warn('[TTSControls] 未提取到文本');
    }
  }, [extractPageText, speak, requestWakeLock, checkSynthAvailable]);

  // 自动朗读整个章节
  const handleAutoReadChapter = useCallback(async () => {
    if (!checkSynthAvailable()) {
      setStatusText('当前浏览器不支持语音朗读');
      console.warn('[TTSControls] speechSynthesis 不可用');
      return;
    }
    console.log('[TTSControls] ========== 开始自动朗读章节 ==========');
    autoReadRef.current = true;
    await requestWakeLock();

    const readPages: string[] = [];
    let pageCount = 0;
    const MAX_PAGES = 500;

    while (autoReadRef.current && pageCount < MAX_PAGES) {
      // 提取当前页文本
      setStatusText(`正在提取第 ${pageCount + 1} 页...`);
      const text = await extractPageText();
      if (!text || text.length < 10) {
        console.log('[TTSControls] 当前页无有效文本，结束');
        break;
      }

      // 检测重复页面（章节结束标志）
      const textHash = text.substring(0, 200);
      if (readPages.some(p => p === textHash)) {
        console.log('[TTSControls] 检测到重复页面，章节已结束');
        break;
      }

      readPages.push(textHash);
      pageCount++;
      setStatusText(`朗读第 ${pageCount} 页...`);
      console.log(`[TTSControls] 朗读第 ${pageCount} 页，长度: ${text.length}`);

      // 朗读当前页（等待完成）
      await speak(text);

      // 检查是否被停止
      if (!autoReadRef.current) break;

      // 翻到下一页
      setStatusText('翻页中...');
      const hasNext = await goToNextPage();
      if (!hasNext) {
        console.log('[TTSControls] 翻页失败，结束');
        break;
      }

      // 等待页面渲染
      await waitForPageLoad();

      // 再次检查是否被停止
      if (!autoReadRef.current) break;
    }

    console.log(`[TTSControls] 自动朗读完成，共 ${pageCount} 页`);
    setStatusText('');
    autoReadRef.current = false;
    await releaseWakeLock();
  }, [extractPageText, speak, goToNextPage, waitForPageLoad, requestWakeLock, releaseWakeLock]);

  // 停止自动朗读
  const handleStop = useCallback(() => {
    autoReadRef.current = false;
    ttsService.stop();
    setStatusText('');
    releaseWakeLock();
    console.log('[TTSControls] 已停止');
  }, [ttsService, releaseWakeLock]);

  // 播放/暂停
  const togglePlayPause = useCallback(() => {
    try {
      if (isPaused) {
        ttsService.resume();
      } else if (isSpeaking) {
        ttsService.pause();
      }
    } catch (e) {
      console.warn('[TTS] play/pause 切换失败:', e);
    }
  }, [isSpeaking, isPaused, ttsService]);

  // 速度变化（问题5：立即生效）
  const handleSpeedChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSpeed = parseFloat(e.target.value);
    setSpeed(newSpeed);
    ttsService.updateConfig({ rate: newSpeed });
    // 如果正在朗读，需要重启当前块以应用新语速
    if (ttsSpeakingRef.current) {
      console.log('[TTSControls] 语速变更，重启当前朗读以应用');
      // 保存当前状态，稍后重启
      const state = ttsService.getState();
      const currentText = state.currentText;
      const chunks = currentText;
      // 停止并从头开始（简单方案：重新朗读剩余部分）
      ttsService.stop();
      // 重新朗读剩余的文本
      setTimeout(() => {
        if (currentText) {
          speak(currentText);
        }
      }, 100);
    }
  }, [ttsService, speak]);

  // 语音角色变化
  const handleVoiceChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const name = e.target.value;
    setSelectedVoice(name);
    ttsService.updateConfig({ voiceName: name });
  }, [ttsService]);

  // === 渲染 ===

  if (isMinimized) {
    return (
      <div
        ref={controlsRef}
        className="tts-controls tts-minimized"
        style={{ left: '50%', top: '20px', transform: 'translateX(-50%)' }}
        onClick={onMinimize}
        onTouchEnd={(e) => { e.preventDefault(); onMinimize(); }}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
        </svg>
        <span>TTS</span>
        {statusText && <span className="tts-status-text">{statusText}</span>}
      </div>
    );
  }

  return (
    <div
      ref={controlsRef}
      className="tts-controls"
      style={isCentered
        ? { left: '50%', top: '20px', transform: 'translateX(-50%)' }
        : { left: posRef.current.x + 'px', top: posRef.current.y + 'px', transform: 'none' }
      }
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <div className="tts-drag-handle">
        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
          <path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
        </svg>
      </div>

      {/* 朗读当前页 */}
      <button
        className="tts-btn tts-btn-read"
        onClick={handleReadPage}
        title="朗读当前页"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
        </svg>
      </button>

      {/* 自动朗读章节 / 停止 */}
      <button
        className={`tts-btn tts-btn-auto ${isSpeaking ? 'active' : ''}`}
        onClick={isSpeaking ? handleStop : handleAutoReadChapter}
        title={isSpeaking ? '停止自动朗读' : '自动朗读整个章节'}
        style={{ background: isSpeaking ? 'rgba(244, 67, 54, 0.8)' : 'rgba(76, 175, 80, 0.8)' }}
      >
        {isSpeaking ? (
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M6 6h12v12H6z"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8 12.5v-9l6 4.5-6 4.5z"/>
          </svg>
        )}
      </button>

      {/* 播放/暂停 */}
      <button
        className={`tts-btn ${isSpeaking && !isPaused ? 'active' : ''}`}
        onClick={togglePlayPause}
        title={isPaused ? '继续' : isSpeaking ? '暂停' : '播放'}
        disabled={!isSpeaking && !isPaused}
      >
        {isPaused || !isSpeaking ? (
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M8 5v14l11-7z"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
          </svg>
        )}
      </button>

      {/* 停止 */}
      <button
        className="tts-btn"
        onClick={handleStop}
        title="停止"
        disabled={!isSpeaking}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
          <path d="M6 6h12v12H6z"/>
        </svg>
      </button>

      {/* 进度条 */}
      <div className="tts-progress">
        <div
          className="tts-progress-bar"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* 语速选择 */}
      <div className="tts-speed">
        <select value={speed} onChange={handleSpeedChange}>
          <option value={0.5}>0.5x</option>
          <option value={0.75}>0.75x</option>
          <option value={1}>1x</option>
          <option value={1.25}>1.25x</option>
          <option value={1.5}>1.5x</option>
          <option value={2}>2x</option>
          <option value={3}>3x</option>
        </select>
      </div>

      {/* 语音角色选择 */}
      {voices.length > 1 && (
        <div className="tts-voice">
          <select value={selectedVoice} onChange={handleVoiceChange}>
            {voices.map(v => (
              <option key={v.name} value={v.name}>
                {v.name.substring(0, 15)} ({v.lang})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* 状态文字 */}
      {statusText && <span className="tts-status-text">{statusText}</span>}

      {/* 关闭 */}
      <button
        className="tts-btn tts-close"
        onClick={onClose}
        title="关闭"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>
  );
};

export default TTSControls;
